import React, { useContext } from 'react';
import { Button } from 'antd';
import { ModalContext } from 'context/ModalContext';
import styled from 'styled-components';
import Config from 'components/widgets/config';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { fetch_product_details, publish_product } from 'services/apiSrv';
import AddProductBenefit from 'components/widgets/modals/AddProductBenefit';
import { show_toast } from 'utils/helpers';

// --- Styled Components ---
const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media (min-width: 1024px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const HeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: bold;
  color: #111827;
  margin: 0;
`;

const MetaWrapper = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  color: #6b7280;
  font-size: 0.875rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  @media (min-width: 640px) {
    margin-top: 0;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: #6366f1;
  border: none;
  color: white;
  font-weight: 600;
  &:hover {
    background-color: #4f46e5;
    color: white;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-weight: 500;
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
  }
`;

const DetailsSection = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 32px;
  margin-bottom: 32px;
  flex-wrap: wrap;
`;

const DetailsCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  flex: 1;
  min-width: 300px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const InfoText = styled.p`
  margin: 8px 0;
  font-size: 0.875rem;
  color: #374151;
`;

const RoundedContainer = styled.div`
  border-radius: 0.25rem;
  margin: 1em 0;
`;

const FluidImage = styled.img`
  border-radius: 0.25rem;
  max-width: 12em;
  height: auto;
`;

// --- Component ---
function ViewProduct() {
  const { modalShow, modalClose } = useContext(ModalContext);
  const history = useHistory();
  const { id: productSystemRef } = useParams();

  const { data, isLoading, isSuccess, isError, error, refetch } = useQuery({
    queryKey: ['productDetails', productSystemRef],
    queryFn: () => fetch_product_details(productSystemRef),
    enabled: !!productSystemRef,
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });

  const publishProduct = async (status) => {
    try {
      await publish_product(productSystemRef, status);
      await refetch();
      show_toast(status ? 'Product published successfully!' : 'Unpublished successfully!', 'success');
    } catch (err) {
      show_toast(`Failed: ${err?.message || 'Something went wrong'}`, 'error');
    }
  };

  if (isLoading) return <Container>Loading product details...</Container>;
  if (isError) return <Container>Error: {error?.message}</Container>;
  if (!data?.data) return <Container>No product data available.</Container>;

  const product = data.data[0];
  const { productName, productLogo, productDescription, isPublished, productPremium, basePremiumRate } = product;

  return (
    <Container>
      {/* --- Header --- */}
      <HeaderWrapper>
        <HeaderInfo>
          <Title>{productName || 'Unnamed Product'}</Title>
          <MetaWrapper>
            <MetaItem>Premium Min: {productPremium?.minimum || 'N/A'}</MetaItem>
            <MetaItem>Premium Max: {productPremium?.maximum || 'N/A'}</MetaItem>
            <MetaItem>Rate Type: {basePremiumRate?.rateType || 'N/A'}</MetaItem>
          </MetaWrapper>
        </HeaderInfo>

        <ActionsWrapper>
          <SecondaryButton onClick={() => history.push(`/dashboard/setup/product/${productSystemRef}/edit`)}>Edit</SecondaryButton>
          <PrimaryButton onClick={() => publishProduct(!isPublished)}>
            {isPublished ? 'Unpublish' : 'Publish'}
          </PrimaryButton>
        </ActionsWrapper>
      </HeaderWrapper>

      {/* --- Details --- */}
      <DetailsSection>
        <DetailsCard>
          <RoundedContainer>
            <FluidImage src={productLogo} alt={productName} />
          </RoundedContainer>
          <InfoText>Description: {productDescription || 'No description available'}</InfoText>
          <InfoText>Status: {isPublished ? 'Published ✅' : 'Not Published ❌'}</InfoText>
        </DetailsCard>
      </DetailsSection>

      {/* --- Config + Benefits Modal --- */}
      <AddProductBenefit show={modalShow} onHide={modalClose} fetchBenefits={refetch} />
     {isSuccess && <Config productSystemRef={productSystemRef}  product={product}/>} 
    </Container>
  );
}

export default ViewProduct;
